import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useFooterData } from '../contexts/FooterContext';
import { API, toUrlSlug } from '../utils/api';

// Footer Component
const Footer = () => {
  const { footerData } = useFooterData();
  const [popularPages, setPopularPages] = useState([]);
  
  // Fetch popular wine pages (combinations with 10+ deals)
  useEffect(() => {
    const fetchPopularPages = async () => {
      try {
        const response = await axios.get(`${API}/wine-deals/combinations?min_count=10`);
        if (response.data.success) {
          setPopularPages(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching popular pages:', error);
      }
    };
    
    fetchPopularPages();
  }, []);
  
  // Default values as fallback
  const defaultCountries = ['USA', 'France', 'Italy', 'Portugal', 'Spain', 'Argentina', 'Australia', 'New Zealand'];
  const defaultWineTypes = [
    { name: 'Red Wine', path: 'red-wine' },
    { name: 'White Wine', path: 'white-wine' },
    { name: 'Rosé Wine', path: 'rose-wine' },
    { name: 'Sparkling Wine', path: 'sparkling-wine' }
  ];

  const displayCountries = footerData.countries.length > 0 ? footerData.countries : defaultCountries;
  const displayWineTypes = footerData.wineTypes.length > 0 ? footerData.wineTypes : defaultWineTypes;

  return (
    <footer style={{backgroundColor: '#292021'}} className="text-white py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-2xl">🍷</span>
              <span className="text-xl font-bold" style={{color: '#E9D8D5'}}>Great Wine Deals</span>
            </div>
            <p style={{color: '#A98D8C'}} className="text-sm">
              Curated selection of delicious wines under $30 from wineries around the world.
            </p>
          </div>

          {/* Countries */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#E9D8D5'}}>Browse by Country</h3>
            <div className="grid grid-cols-2 gap-2">
              {displayCountries.map(country => (
                <Link
                  key={country}
                  to={`/country/${toUrlSlug(country)}`}
                  style={{color: '#A98D8C'}}
                  className="hover:text-white text-sm transition-colors"
                >
                  {country}
                </Link>
              ))}
            </div>
          </div>

          {/* Wine Types */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#E9D8D5'}}>Browse by Wine Type</h3>
            <div className="space-y-2">
              {displayWineTypes.map(type => (
                <Link
                  key={type.path}
                  to={`/type/${type.path}`}
                  style={{color: '#A98D8C'}}
                  className="block hover:text-white text-sm transition-colors"
                >
                  {type.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        
        {/* Popular Wine Pages - only show if we have data */}
        {popularPages.length > 0 && (
          <div className="mt-8 pt-8" style={{borderTop: '1px solid #5D0F0F'}}>
            <h3 className="text-lg font-semibold mb-4" style={{color: '#E9D8D5'}}>Popular Wine Pages</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {popularPages.map(page => (
                <Link
                  key={page.page_id}
                  to={`/${page.url_path}`}
                  style={{color: '#A98D8C'}}
                  className="hover:text-white text-sm transition-colors"
                >
                  {page.display_name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Buy Me a Coffee */}
        <div className="mt-8 pt-8 text-center">
          <a 
            href="https://www.buymeacoffee.com/greatwinedeals" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block transition-transform hover:scale-105"
          >
            <img 
              src="https://img.buymeacoffee.com/button-api/?text=Buy me a glass&emoji=🍷&slug=greatwinedeals&button_colour=9350ef&font_colour=ffffff&font_family=Poppins&outline_colour=ffffff&coffee_colour=FFDD00" 
              alt="Buy me a glass"
              width="217"
              height="50"
              loading="lazy"
              style={{height: '50px', width: 'auto'}}
            />
          </a>
        </div>

        {/* Pricing Disclaimer */}
        <div style={{borderColor: '#5D0F0F'}} className="border-t mt-8 pt-8">
          <p style={{color: '#A98D8C'}} className="text-xs max-w-4xl mx-auto text-center leading-relaxed">
            <strong>Disclaimer:</strong> Prices and availability are subject to change without notice. The price listed was recorded at the time of entry based on a New Jersey (NJ) location. Actual pricing may vary significantly depending on your local store, state taxes, and regional promotions. Please verify the final price on the retailer's website before completing your purchase.
          </p>
        </div>

        {/* Bottom */}
        <div style={{borderColor: '#5D0F0F'}} className="border-t mt-8 pt-8 text-center">
          <p style={{color: '#A98D8C'}} className="text-sm">
            © 2025 Great Wine Deals. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
